import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";import { Calendar, Gauge, Fuel, Cog, Palette, Car as CarIcon, ChevronLeft, ChevronRight, ShieldCheck, Phone, ExternalLink, Share2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LeadForm } from "@/components/LeadForm";
import { SEO } from "@/components/SEO";
import { api, formatPrice, formatKm, resolveImage } from "@/lib/api";

export default function CarDetailPage() {
    const { id } = useParams();
    const [car, setCar] = useState(null);
    const [idx, setIdx] = useState(0);
    const [tab, setTab] = useState("proefrit");

    useEffect(() => {
        api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setCar(false));
    }, [id]);

    const shareWhatsApp = () => {
        const text = `Bekijk deze ${car.merk} ${car.model} (${car.bouwjaar}) bij Used Car Selection — ${formatPrice(car.prijs)}\n${window.location.href}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    };

    if (car === null) return <div><Navbar /><div className="ucs-container py-32 text-center text-slate-500">Laden...</div></div>;
    if (car === false) return (
        <div><Navbar />
            <div className="ucs-container py-32 text-center">
                <h2 className="text-2xl font-bold mb-4">Auto niet gevonden</h2>
                <Link to="/aanbod" className="ucs-btn-accent">Bekijk aanbod</Link>
            </div>
        </div>
    );

    const imgs = car.afbeeldingen?.length ? car.afbeeldingen : [];

    // Schema.org Vehicle + Product JSON-LD voor Google rich snippets
    // (prijs, km, brandstof verschijnen direct in zoekresultaten)
    const fuelMap = {
        "Benzine": "https://schema.org/PetrolFuel",
        "Diesel": "https://schema.org/DieselFuel",
        "Elektrisch": "https://schema.org/ElectricFuel",
        "Hybride": "https://schema.org/HybridFuel",
        "LPG / CNG": "https://schema.org/LPGFuel",
    };
    const transmissionMap = {
        "Automaat": "https://schema.org/AutomatedVehicleTransmission",
        "Manueel": "https://schema.org/ManualTransmission",
    };
    const carImageUrl = imgs[0] ? resolveImage(imgs[0]) : undefined;
    const carUrl = `${typeof window !== "undefined" ? window.location.origin : "https://usedcarselection.be"}/auto/${car.id}`;
    const vehicleJsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": `${car.merk} ${car.model}`,
        "description": (car.beschrijving || "").slice(0, 400),
        "image": carImageUrl ? [carImageUrl] : undefined,
        "brand": { "@type": "Brand", "name": car.merk },
        "sku": car.external_id || car.id,
        "url": carUrl,
        "offers": {
            "@type": "Offer",
            "url": carUrl,
            "priceCurrency": "EUR",
            "price": car.prijs,
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/UsedCondition",
            "seller": { "@type": "AutoDealer", "name": "Used Car Selection" },
        },
        "additionalProperty": [
            { "@type": "PropertyValue", "name": "Bouwjaar", "value": car.bouwjaar },
            { "@type": "PropertyValue", "name": "Brandstof", "value": car.brandstof },
            { "@type": "PropertyValue", "name": "Transmissie", "value": car.transmissie },
            car.vermogen && { "@type": "PropertyValue", "name": "Vermogen", "value": `${car.vermogen} pk` },
            car.kleur && { "@type": "PropertyValue", "name": "Kleur", "value": car.kleur },
        ].filter(Boolean),
        // Vehicle-specifieke velden binnen Product (Google leest beide)
        "vehicleEngine": car.cilinderinhoud ? { "@type": "EngineSpecification",
            "engineDisplacement": { "@type": "QuantitativeValue", "value": car.cilinderinhoud, "unitCode": "CMQ" } } : undefined,
        "mileageFromOdometer": {
            "@type": "QuantitativeValue", "value": car.kilometerstand, "unitCode": "KMT",
        },
        "vehicleModelDate": String(car.bouwjaar),
        "fuelType": fuelMap[car.brandstof] || car.brandstof,
        "vehicleTransmission": transmissionMap[car.transmissie] || car.transmissie,
        "color": car.kleur || undefined,
        "numberOfDoors": car.deuren || undefined,
        "bodyType": car.carrosserie || undefined,
    };

    const specs = [
        { icon: <Calendar />, label: "Bouwjaar", value: car.bouwjaar },
        { icon: <Gauge />, label: "Kilometerstand", value: formatKm(car.kilometerstand) },
        { icon: <Fuel />, label: "Brandstof", value: car.brandstof },
        { icon: <Cog />, label: "Transmissie", value: car.transmissie },
        car.vermogen && { icon: <CarIcon />, label: "Vermogen", value: `${car.vermogen} pk` },
        car.kleur && { icon: <Palette />, label: "Kleur", value: car.kleur },
        car.carrosserie && { icon: <CarIcon />, label: "Carrosserie", value: car.carrosserie },
        car.deuren && { icon: <CarIcon />, label: "Deuren", value: car.deuren },
        car.apk_tot && { icon: <ShieldCheck />, label: "APK tot", value: car.apk_tot },
    ].filter(Boolean);

    return (
        <div>
            <SEO
                title={`${car.merk} ${car.model}`}
                description={`${car.merk} ${car.model} (${car.bouwjaar}) — ${formatKm(car.kilometerstand)}, ${car.brandstof}, ${car.transmissie}. Vraagprijs ${formatPrice(car.prijs)}. Beschikbaar bij Used Car Selection in Bornem.`}
                image={car.afbeeldingen?.[0] ? resolveImage(car.afbeeldingen[0]) : undefined}
                jsonLd={vehicleJsonLd}
            />
            <Navbar />

            <div className="bg-white border-b border-slate-200">
                <div className="ucs-container py-4 text-xs text-slate-500">
                    <Link to="/" className="hover:text-[#EC6A0A]">Home</Link> /{" "}
                    <Link to="/aanbod" className="hover:text-[#EC6A0A]">Aanbod</Link> /{" "}
                    <span className="text-[#0E2C4D] font-bold">{car.merk} {car.model}</span>
                </div>
            </div>

            <section className="ucs-container py-10 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                    <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden" style={{borderRadius: 2}} data-testid="detail-gallery">
                        {imgs[idx] ? (
                            <img src={resolveImage(imgs[idx])} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">Geen afbeelding</div>
                        )}
                        {imgs.length > 1 && (
                            <>
                                <button onClick={() => setIdx((idx - 1 + imgs.length) % imgs.length)}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 p-2.5 hover:bg-white" data-testid="gallery-prev">
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button onClick={() => setIdx((idx + 1) % imgs.length)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 p-2.5 hover:bg-white" data-testid="gallery-next">
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </>
                        )}
                    </div>
                    {imgs.length > 1 && (
                        <div className="flex gap-3 mt-4 overflow-x-auto no-scrollbar">
                            {imgs.map((u, i) => (
                                <button key={i} onClick={() => setIdx(i)}
                                        className={`flex-shrink-0 w-24 h-20 overflow-hidden border-2 ${idx === i ? "border-[#EC6A0A]" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
                                    <img src={resolveImage(u)} className="w-full h-full object-cover" alt="" loading="lazy" decoding="async" />
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="mt-10">
                        <div className="label-eyebrow mb-2">{car.merk}</div>
                        <h1 className="text-3xl md:text-4xl font-black text-[#0E2C4D] tracking-tight" data-testid="detail-title">
                            {car.model}
                        </h1>
                        <p className="mt-5 text-slate-600 leading-relaxed">{car.beschrijving}</p>

                        <div className="mt-10">
                            <h3 className="text-xl font-bold text-[#0E2C4D] mb-5">Specificaties</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {specs.map((s) => (
                                    <div key={s.label} className="bg-white border border-slate-200 p-4" style={{borderRadius: 2}}>
                                        <div className="flex items-center gap-2 text-[#EC6A0A] mb-2">{s.icon}</div>
                                        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">{s.label}</div>
                                        <div className="font-bold text-[#0E2C4D] mt-1">{s.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {car.opties?.length > 0 && (
                            <div className="mt-10">
                                <h3 className="text-xl font-bold text-[#0E2C4D] mb-5">Opties & accessoires</h3>
                                <div className="flex flex-wrap gap-2">
                                    {car.opties.map((o) => (
                                        <span key={o} className="px-3 py-2 bg-white border border-slate-200 text-sm" style={{borderRadius: 2}}>
                                            {o}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sticky sidebar */}
                <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start space-y-6">
                    <div className="bg-[#0E2C4D] text-white p-8" style={{borderRadius: 2}}>
                        <div className="label-eyebrow text-[#EC6A0A] mb-2">Vraagprijs</div>
                        <div className="text-5xl font-black" data-testid="detail-price">{formatPrice(car.prijs)}</div>
                        <div className="mt-2 text-sm text-white/60">12 maanden wettelijke garantie</div>

                        <div className="mt-6 grid grid-cols-2 gap-2">
                            <a href="tel:+32479442798" className="flex items-center justify-center gap-2 bg-[#EC6A0A] hover:bg-[#c95a06] text-white px-3 py-3 text-sm font-bold transition-colors"
                               style={{borderRadius: 2}} data-testid="detail-call">
                                <Phone className="w-4 h-4" /> Bel ons
                            </a>
                            <button onClick={shareWhatsApp}
                                    className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/15 text-white px-3 py-3 text-sm font-bold transition-colors"
                                    style={{borderRadius: 2}} data-testid="detail-whatsapp-share">
                                <Share2 className="w-4 h-4" /> WhatsApp
                            </button>
                        </div>

                        <div className="mt-8 flex bg-white/5 p-1" style={{borderRadius: 2}}>
                            {[["proefrit", "Proefrit"], ["vraag", "Stel een vraag"]].map(([k, l]) => (
                                <button key={k} onClick={() => setTab(k)}
                                        className={`flex-1 px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${tab === k ? "bg-[#EC6A0A] text-white" : "text-white/70 hover:text-white"}`}
                                        data-testid={`detail-tab-${k}`} style={{borderRadius: 2}}>
                                    {l}
                                </button>
                            ))}
                        </div>

                        <div className="mt-6 space-y-3 text-sm">
                            <div className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-[#EC6A0A]" /> 12 maanden wettelijke garantie</div>
                            <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#EC6A0A]" /> Gratis advies: +32 479 44 27 98</div>
                        </div>

                        {car.tweedehands_url && (
                            <a href={car.tweedehands_url} target="_blank" rel="noopener noreferrer"
                               className="mt-6 flex items-center justify-between gap-2 bg-white/5 hover:bg-white/10 border border-white/15 px-4 py-3 text-sm transition-colors"
                               style={{borderRadius: 2}} data-testid="detail-tweedehands-link">
                                <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-[#EC6A0A] rounded-full" />
                                    Bekijk deze wagen ook op 2dehands.be
                                </span>
                                <ExternalLink className="w-4 h-4 text-[#EC6A0A]" />
                            </a>
                        )}
                    </div>

                    <LeadForm
                        autoId={car.id}
                        type={tab}
                        title={tab === "proefrit" ? "Plan een proefrit" : "Stel een vraag"}
                        subtitle={tab === "proefrit" ? "We plannen uw proefrit op een moment dat u past." :
                                  "Een adviseur reageert binnen één werkdag."}
                    />
                </aside>
            </section>

            <Footer />
        </div>
    );
}
